import { COUNTRIES, INITIAL_FORM_STATE, INPUT_CLASS, LABEL_CLASS } from '@/constants/constants';
import { WarningMessage } from './WarningMessage';

interface ContactFormRightPanelProps {
  form: typeof INITIAL_FORM_STATE;
  errors: Partial<Record<keyof typeof INITIAL_FORM_STATE, string>>;
  handleChange: (field: keyof typeof INITIAL_FORM_STATE) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  setForm: React.Dispatch<React.SetStateAction<typeof INITIAL_FORM_STATE>>;
  onClose: () => void;
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
}

const ContactFormRightPanel: React.FC<ContactFormRightPanelProps> = ({
  form,
  errors,
  handleChange,
  setForm,
  onClose,
  onSubmit,
}) => {
  return (
    <div className="w-full lg:w-1/2 flex flex-col pt-3 pb-8 relative bg-white h-full max-h-[95vh] sm:max-h-none overflow-hidden">
      <div className="flex justify-end px-6 pb-2">
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-700">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="currentColor" />
          </svg>
        </button>
      </div>
      <form
        className="flex px-6 md:px-12 w-full flex-col items-start gap-5 flex-1 overflow-y-auto"
        id="contactForm"
        noValidate
        onSubmit={onSubmit}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 sm:gap-y-1 w-full">
          {/* Name */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Name <span className="text-[#da1b41] ml-1">*</span>
            </label>
            <input
              required
              minLength={2}
              placeholder="e.g. John Smith"
              value={form.name}
              onChange={handleChange('name')}
              className={`${INPUT_CLASS} relative z-10 ${errors.name ? 'border-[#da1b41] focus:border-[#da1b41] focus:ring-[#da1b41]' : ''}`}
            />
            {errors.name && <WarningMessage message={errors.name} />}
          </div>

          {/* Company/Organization */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Company/Organization <span className="text-[#da1b41] ml-1">*</span>
            </label>
            <input
              required
              minLength={2}
              placeholder="e.g. SixSense.ai"
              value={form.company}
              onChange={handleChange('company')}
              className={`${INPUT_CLASS} relative z-10 ${errors.company ? 'border-[#da1b41] focus:border-[#da1b41] focus:ring-[#da1b41]' : ''}`}
            />
            {errors.company && <WarningMessage message={errors.company} />}
          </div>

          {/* Work email */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Work email <span className="text-[#da1b41] ml-1">*</span>
            </label>
            <input
              required
              type="email"
              placeholder="e.g. john@company.ai"
              value={form.email}
              onChange={handleChange('email')}
              className={`${INPUT_CLASS} relative z-10 ${errors.email ? 'border-[#da1b41] focus:border-[#da1b41] focus:ring-[#da1b41]' : ''}`}
            />
            {errors.email && <WarningMessage message={errors.email} />}
          </div>

          {/* Job title */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Job title <span className="text-[#da1b41] ml-1">*</span>
            </label>
            <input
              required
              minLength={2}
              placeholder="e.g. Engineering Manager"
              value={form.jobTitle}
              onChange={handleChange('jobTitle')}
              className={`${INPUT_CLASS} relative z-10 ${errors.jobTitle ? 'border-[#da1b41] focus:border-[#da1b41] focus:ring-[#da1b41]' : ''}`}
            />
            {errors.jobTitle && <WarningMessage message={errors.jobTitle} />}
          </div>

          {/* Reason for inquiry */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Reason for inquiry <span className="text-[#da1b41] ml-1">*</span>
            </label>
            <input
              required
              minLength={2}
              placeholder="e.g. Sales demo"
              value={form.reason}
              onChange={handleChange('reason')}
              className={`${INPUT_CLASS} relative z-10 ${errors.reason ? 'border-[#da1b41] focus:border-[#da1b41] focus:ring-[#da1b41]' : ''}`}
            />
            {errors.reason && <WarningMessage message={errors.reason} />}
          </div>

          {/* Where are you located? */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Where are you located? <span className="text-[#8599ad] italic ml-1">(Optional)</span>
            </label>
            <select
              value={form.location}
              onChange={handleChange('location')}
              className={`${INPUT_CLASS} appearance-none bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2212%22%20height%3D%228%22%20viewBox%3D%220%200%2012%208%22%20fill%3D%22none%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M1.41%200.589966L6%205.16997L10.59%200.589966L12%201.99997L6%207.99997L0%201.99997L1.41%200.589966Z%22%20fill%3D%22%239ca3af%22/%3E%3C/svg%3E')] bg-[length:12px_8px] bg-no-repeat bg-[center_right_1rem]`}
            >
              <option value="" disabled>Select</option>
              {COUNTRIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Contact number */}
          <div className="flex w-full flex-col items-start justify-end h-full relative mb-5 sm:mb-6">
            <label className={LABEL_CLASS}>
              Contact number <span className="text-[#8599ad] italic ml-1">(Optional)</span>
            </label>
            <input
              type="tel"
              placeholder="e.g. +1 (555) 000-0000"
              value={form.contactNo}
              onChange={(e) => {
                const filtered = e.target.value.replace(/[a-zA-Z]/g, '');
                setForm(prev => ({ ...prev, contactNo: filtered }));
              }}
              className={INPUT_CLASS}
            />
          </div>
        </div>

        <div className="flex w-full flex-col items-start mt-2">
          <label className={LABEL_CLASS}>
            How can we customize your demo? <span className="text-[#8599ad] italic ml-1">(Optional)</span>
          </label>
          <textarea
            rows={4}
            placeholder="e.g. I am interested in learning more about SixSense's classifAI software."
            value={form.message}
            onChange={handleChange('message')}
            className="w-full h-[96px] rounded-[4px] border border-[#d7e7ff] bg-[#fafcff] px-[14px] py-[10px] text-[#212a31] text-[14px] font-ibm-plex font-normal leading-[1.4] outline-none placeholder:text-[#b6c2ce] focus:border-[#1449ea] focus:ring-1 focus:ring-[#1449ea] transition-all resize-none"
          ></textarea>
        </div>

        <div className="w-full flex justify-end mt-4">
          <button
            type="submit"
            className="h-[40px] px-[12px] py-[8px] flex justify-center items-center rounded-[4px] bg-[#144aeb] hover:bg-[#113cbd] transition-colors text-[#f4f5f6] font-ibm-plex text-[14px] font-[600] leading-[24px]"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactFormRightPanel;
